/**
 * The Desktop's windows — folder windows and file viewers — in one stacking
 * order.
 *
 * A FOLDER WINDOW has its own id and shows one container at a time (`root` =
 * Documents, a folder, or the Trash). Opening a folder inside it navigates
 * it, with Back / Forward / Up like a file manager; "Open in new window"
 * makes another. Folder windows are part of the person's saved arrangement
 * (their geometry and what they show persist; the history does not).
 *
 * A VIEWER shows one file (an image, a PDF, a document…). Viewers are for
 * this visit only — reopening the workspace doesn't reopen files.
 *
 * Both kinds share one z-order: raising either brings it above the rest.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import type { VaultDocumentDto } from '@/lib/api';
import type { DesktopLayoutController } from '../../hooks/useDesktopLayout';
import {
  MIN_WINDOW,
  cascadeWindow,
  clampWindow,
  emptyPatch,
  newWindowId,
  nextZ,
  type WindowGeometry,
} from '../../lib/desktop-layout';
import { cssEscape } from './desktop-model';

/** Above this, z-orders are renumbered so they never hit the stored cap. */
const Z_RENUMBER_AT = 10_000;

export interface ViewerWindow {
  id: string;
  doc: VaultDocumentDto;
  geometry: WindowGeometry;
}

export interface WindowOpener {
  container: string;
  key: string;
}

interface History {
  back: string[];
  forward: string[];
}

export function useDesktopWindows(input: {
  layout: DesktopLayoutController;
  areaSize: { width: number; height: number };
  /** Is this container still there (a folder can be deleted elsewhere)? */
  exists: (container: string) => boolean;
}) {
  const { layout, areaSize, exists } = input;
  const { enqueue } = layout;
  const windows = layout.layout.windows;
  const [viewers, setViewers] = useState<ViewerWindow[]>([]);
  const [focusedWindow, setFocusedWindow] = useState<string | null>(null);
  const [focusRequests, setFocusRequests] = useState<Record<string, number>>({});
  const openers = useRef(new Map<string, WindowOpener>());
  const histories = useRef(new Map<string, History>());
  const [, setHistoryVersion] = useState(0);

  const requestFocus = useCallback(
    (id: string) => setFocusRequests((prev) => ({ ...prev, [id]: (prev[id] ?? 0) + 1 })),
    [],
  );

  const setWindow = useCallback(
    (id: string, geometry: WindowGeometry | null) => {
      const patch = emptyPatch();
      patch.windows.set(id, geometry);
      enqueue(patch);
    },
    [enqueue],
  );

  /** The next z above every window, folder or viewer. */
  const topZ = useCallback(() => {
    let max = nextZ(windows) - 1;
    for (const v of viewers) max = Math.max(max, v.geometry.z);
    return max + 1;
  }, [viewers, windows]);

  const raise = useCallback(
    (id: string, extra: Partial<WindowGeometry> = {}) => {
      const viewer = viewers.find((v) => v.id === id);
      if (viewer) {
        const z = topZ();
        setViewers((prev) =>
          prev.map((v) => (v.id === id ? { ...v, geometry: { ...v.geometry, ...extra, z } } : v)),
        );
        setFocusedWindow(id);
        return;
      }
      const current = windows[id];
      if (!current) return;
      const top = topZ();
      const isTop =
        Object.entries(windows).every(([k, g]) => k === id || g.z < current.z) &&
        viewers.every((v) => v.geometry.z < current.z);
      if (top > Z_RENUMBER_AT) {
        const patch = emptyPatch();
        const order = Object.entries(windows)
          .sort((a, b) => a[1].z - b[1].z)
          .map(([k]) => k);
        order
          .filter((k) => k !== id)
          .forEach((k, i) => patch.windows.set(k, { ...windows[k], z: i + 1 }));
        patch.windows.set(id, { ...current, ...extra, z: order.length });
        enqueue(patch);
        setViewers((prev) =>
          prev.map((v, i) => ({ ...v, geometry: { ...v.geometry, z: order.length + 1 + i } })),
        );
      } else if (!isTop || Object.keys(extra).length > 0) {
        setWindow(id, { ...current, ...extra, z: isTop ? current.z : top });
      }
      setFocusedWindow(id);
    },
    [enqueue, setWindow, topZ, viewers, windows],
  );

  /** The open window showing `container`, if any (the frontmost). */
  const windowShowing = useCallback(
    (container: string): string | undefined =>
      Object.entries(windows)
        .filter(([, g]) => g.folder === container)
        .sort((a, b) => b[1].z - a[1].z)[0]?.[0],
    [windows],
  );

  /**
   * Show `container` in a window: the one already showing it (raised), or a
   * new one. `newWindow` always makes a new one.
   */
  const openFolder = useCallback(
    (container: string, options: { newWindow?: boolean; opener?: WindowOpener } = {}) => {
      const existing = options.newWindow ? undefined : windowShowing(container);
      if (existing) {
        if (options.opener) openers.current.set(existing, options.opener);
        raise(existing, { min: false });
        requestFocus(existing);
        return existing;
      }
      const id = newWindowId();
      if (options.opener) openers.current.set(id, options.opener);
      const count = Object.keys(windows).length + viewers.length;
      setWindow(id, cascadeWindow(count, topZ(), areaSize, container));
      setFocusedWindow(id);
      requestFocus(id);
      return id;
    },
    [areaSize, raise, requestFocus, setWindow, topZ, viewers.length, windowShowing, windows],
  );

  const historyOf = (id: string): History => {
    let history = histories.current.get(id);
    if (!history) {
      history = { back: [], forward: [] };
      histories.current.set(id, history);
    }
    return history;
  };

  /** Show `container` in window `id`, remembering where it was. */
  const navigate = useCallback(
    (id: string, container: string, via: 'go' | 'back' | 'forward' = 'go') => {
      const current = windows[id];
      if (!current || current.folder === container) return;
      const history = historyOf(id);
      if (via === 'go') {
        history.back.push(current.folder);
        history.forward = [];
      } else if (via === 'back') {
        history.forward.push(current.folder);
      } else {
        history.back.push(current.folder);
      }
      setHistoryVersion((v) => v + 1);
      setWindow(id, { ...current, folder: container, min: false });
      setFocusedWindow(id);
    },
    [setWindow, windows],
  );

  /** Step back / forward, skipping places that no longer exist. */
  const step = useCallback(
    (id: string, direction: 'back' | 'forward') => {
      const history = historyOf(id);
      const stack = direction === 'back' ? history.back : history.forward;
      let target: string | undefined;
      while (stack.length > 0) {
        const candidate = stack.pop()!;
        if (exists(candidate)) {
          target = candidate;
          break;
        }
      }
      if (target) navigate(id, target, direction);
      else setHistoryVersion((v) => v + 1);
    },
    [exists, navigate],
  );

  const canStep = (id: string, direction: 'back' | 'forward') => {
    const history = histories.current.get(id);
    return !!history && (direction === 'back' ? history.back : history.forward).length > 0;
  };

  const focusOpener = useCallback((id: string) => {
    const opener = openers.current.get(id);
    if (!opener) return;
    const el = document.querySelector<HTMLElement>(
      `[data-desktop-container="${cssEscape(opener.container)}"][data-desktop-key="${cssEscape(opener.key)}"]`,
    );
    el?.focus();
  }, []);

  const close = useCallback(
    (id: string) => {
      if (viewers.some((v) => v.id === id)) {
        setViewers((prev) => prev.filter((v) => v.id !== id));
      } else {
        setWindow(id, null);
        histories.current.delete(id);
      }
      setFocusedWindow((f) => (f === id ? null : f));
      requestAnimationFrame(() => focusOpener(id));
    },
    [focusOpener, setWindow, viewers],
  );

  const minimize = useCallback(
    (id: string) => {
      const viewer = viewers.find((v) => v.id === id);
      if (viewer) {
        setViewers((prev) =>
          prev.map((v) => (v.id === id ? { ...v, geometry: { ...v.geometry, min: true } } : v)),
        );
      } else if (windows[id]) {
        setWindow(id, { ...windows[id], min: true });
      }
      setFocusedWindow(null);
      requestAnimationFrame(() => focusOpener(id));
    },
    [focusOpener, setWindow, viewers, windows],
  );

  const commit = useCallback(
    (id: string, next: WindowGeometry) => {
      if (viewers.some((v) => v.id === id)) {
        setViewers((prev) =>
          prev.map((v) =>
            v.id === id ? { ...v, geometry: { ...next, z: v.geometry.z, folder: '' } } : v,
          ),
        );
        return;
      }
      const current = windows[id];
      if (current) setWindow(id, { ...next, z: current.z, folder: current.folder });
    },
    [setWindow, viewers, windows],
  );

  /** Mac green button / Windows maximize: fill the desktop, or restore. */
  const zoomed = useRef(new Map<string, WindowGeometry>());
  const toggleZoom = useCallback(
    (id: string) => {
      const viewer = viewers.find((v) => v.id === id);
      const current = viewer?.geometry ?? windows[id];
      if (!current) return;
      const saved = zoomed.current.get(id);
      const next = saved
        ? { ...saved, z: current.z, folder: current.folder }
        : { ...current, x: 0, y: 0, w: areaSize.width, h: areaSize.height };
      if (saved) zoomed.current.delete(id);
      else zoomed.current.set(id, current);
      commit(id, clampWindow(next, areaSize));
    },
    [areaSize, commit, viewers, windows],
  );

  /** Open a file in a viewer window (the same file twice raises the first). */
  const openViewer = useCallback(
    (doc: VaultDocumentDto, opener?: WindowOpener) => {
      const existing = viewers.find((v) => v.doc.documentId === doc.documentId);
      if (existing) {
        raise(existing.id, { min: false });
        requestFocus(existing.id);
        return existing.id;
      }
      const id = `viewer_${doc.documentId}`;
      if (opener) openers.current.set(id, opener);
      const count = Object.keys(windows).length + viewers.length;
      const base = cascadeWindow(count, topZ(), areaSize, '');
      const geometry = clampWindow(
        {
          ...base,
          w: Math.min(880, Math.max(MIN_WINDOW.w, areaSize.width - 120)),
          h: Math.min(620, Math.max(MIN_WINDOW.h, areaSize.height - 60)),
        },
        areaSize,
      );
      setViewers((prev) => [...prev, { id, doc, geometry }]);
      setFocusedWindow(id);
      requestFocus(id);
      return id;
    },
    [areaSize, raise, requestFocus, topZ, viewers, windows],
  );

  const folderWindows = useMemo(
    () =>
      Object.entries(windows)
        .filter(([, g]) => exists(g.folder))
        .sort((a, b) => a[1].z - b[1].z),
    [exists, windows],
  );

  /** Every window in stacking order, bottom first. */
  const stack = useMemo(() => {
    const all: Array<{ id: string; z: number; min: boolean; kind: 'folder' | 'viewer' }> = [
      ...folderWindows.map(([id, g]) => ({ id, z: g.z, min: g.min, kind: 'folder' as const })),
      ...viewers.map((v) => ({
        id: v.id,
        z: v.geometry.z,
        min: v.geometry.min,
        kind: 'viewer' as const,
      })),
    ];
    return all.sort((a, b) => a.z - b.z);
  }, [folderWindows, viewers]);

  return {
    windows,
    folderWindows,
    viewers,
    stack,
    focusedWindow,
    setFocusedWindow,
    focusRequests,
    requestFocus,
    openFolder,
    navigate,
    back: (id: string) => step(id, 'back'),
    forward: (id: string) => step(id, 'forward'),
    canStep,
    raise,
    close,
    minimize,
    commit,
    toggleZoom,
    openViewer,
    windowShowing,
    focusOpener,
    setWindow,
  };
}

export type DesktopWindowsController = ReturnType<typeof useDesktopWindows>;
